// src/pages/PosReportPage.tsx — для руководителя: продажи кассы и остатки с заказом поставщику
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import { getRole } from "../hooks/usePermissions";
import "./PosPage.css";

interface Sale { dealId: number; number: string; title: string; total: number; at: string; receiptUrl: string | null; pays: string | null; cashier: string }
interface Overview {
  days: number; count: number; total: number; byMethod: Record<string, number>; noReceipt: number; lastSaleAt: string | null;
  byDay: { day: string; n: number; total: number }[]; sales: Sale[];
}
interface Row {
  id: number; nameRu: string; sku: string | null; unit: string; qty: number; minQuantity: number; sold30: number;
  daysLeft: number | null; suggested: number; status: "out" | "low" | "ok"; salePrice: number; purchasePrice: number | null;
}

const money = (v: number) => `${Number(v).toLocaleString("ru-RU")} ₸`;
const METHOD_RU: Record<string, string> = { cash: "Наличные", card: "Карта", kaspi: "Kaspi", transfer: "Перевод", halyk: "Halyk" };
const TZ = "Asia/Almaty";
const fmt = (iso: string) => new Date(iso).toLocaleString("ru-RU", { timeZone: TZ, day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const payText = (p: string | null) => (p ?? "").split(",").filter(Boolean).map((x) => { const [m, a] = x.split(":"); return `${METHOD_RU[m] ?? m} ${money(Number(a))}`; }).join(", ");

type Tab = "sales" | "stock";

export default function PosReportPage() {
  const [tab, setTab] = useState<Tab>("sales");
  return (
    <div className="ps-page ps-rep">
      <div className="ps-top">
        <h2>Касса и остатки</h2>
        <div className="ps-seg">
          <button className={tab === "sales" ? "on" : ""} onClick={() => setTab("sales")}>💰 Продажи кассы</button>
          <button className={tab === "stock" ? "on" : ""} onClick={() => setTab("stock")}>📦 Остатки и заказ</button>
        </div>
      </div>
      {tab === "sales" ? <Sales /> : <Stock />}
    </div>
  );
}

function Sales() {
  const [days, setDays] = useState(1);
  const [d, setD] = useState<Overview | null>(null);
  const load = useCallback(() => { api.get("/pos/overview", { params: { days } }).then((r) => setD(r.data)).catch(() => setD(null)); }, [days]);
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  const max = useMemo(() => Math.max(1, ...(d?.byDay ?? []).map((x) => x.total)), [d]);

  return (
    <>
      <div className="ps-row2">
        <div className="ps-seg">
          {[[1, "Сегодня"], [7, "7 дней"], [30, "30 дней"]].map(([v, l]) => (
            <button key={v} className={days === v ? "on" : ""} onClick={() => setDays(v as number)}>{l}</button>
          ))}
        </div>
        <span className="ps-muted" style={{ fontSize: 13 }}>
          {d?.lastSaleAt ? `Последняя продажа: ${fmt(d.lastSaleAt)}` : "Продаж за период нет"} · обновляется автоматически
        </span>
      </div>

      {d && (
        <>
          <div className="ps-kpis">
            <div className="ps-kpi"><span>Выручка</span><b>{money(d.total)}</b></div>
            <div className="ps-kpi"><span>Чеков</span><b>{d.count}</b></div>
            <div className="ps-kpi"><span>Средний чек</span><b>{money(d.count ? Math.round(d.total / d.count) : 0)}</b></div>
            {Object.keys(d.byMethod).filter((m) => d.byMethod[m] > 0).map((m) => (
              <div className="ps-kpi" key={m}><span>{METHOD_RU[m] ?? m}</span><b>{money(d.byMethod[m])}</b></div>
            ))}
            <div className={`ps-kpi ${d.noReceipt ? "warn" : ""}`}><span>Без фискального чека</span><b>{d.noReceipt}</b></div>
          </div>

          {d.byDay.length > 1 && (
            <div className="ps-card">
              <div className="ps-h">Выручка по дням</div>
              <div className="ps-days" style={{ marginBottom: 18 }}>
                {d.byDay.slice().reverse().map((x) => (
                  <div key={x.day} style={{ height: `${(x.total / max) * 100}%` }} title={`${new Date(x.day).toLocaleDateString("ru-RU")}: ${money(x.total)} (${x.n})`}>
                    <small>{new Date(x.day).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit" })}</small>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="ps-card">
            <div className="ps-h">Продажи</div>
            <div className="ps-scroll">
              <table className="ps-table">
                <thead><tr><th>Время</th><th>№</th><th>Состав</th><th>Оплата</th><th>Кассир</th><th className="num">Сумма</th><th>Чек</th></tr></thead>
                <tbody>
                  {d.sales.length === 0 && <tr><td colSpan={7} className="ps-muted">Нет продаж</td></tr>}
                  {d.sales.map((s) => (
                    <tr key={s.dealId}>
                      <td>{fmt(s.at)}</td><td>{s.number}</td><td>{s.title.replace(/^Касса:\s*/, "")}</td><td>{payText(s.pays)}</td><td>{s.cashier}</td>
                      <td className="num"><b>{money(s.total)}</b></td>
                      <td>{s.receiptUrl ? <a href={s.receiptUrl} target="_blank" rel="noreferrer">открыть</a> : <span className="ps-tag low">нет</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function Stock() {
  const isAdmin = getRole() === "ADMIN";
  const [rows, setRows] = useState<Row[]>([]);
  const [only, setOnly] = useState(true);
  const [q, setQ] = useState("");
  const [qty, setQty] = useState<Record<number, number>>({});
  const [pick, setPick] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/pos/reorder").then((r) => {
      const list: Row[] = r.data;
      setRows(list);
      const q0: Record<number, number> = {}; const p0: Record<number, boolean> = {};
      list.forEach((x) => { q0[x.id] = x.suggested; p0[x.id] = x.status !== "ok" && x.suggested > 0; });
      setQty(q0); setPick(p0);
    }).finally(() => setLoading(false));
  }, []);

  const term = q.trim().toLowerCase();
  const shown = rows.filter((r) => (!only || r.status !== "ok") && (!term || r.nameRu.toLowerCase().includes(term) || (r.sku ?? "").toLowerCase().includes(term)));
  const chosen = rows.filter((r) => pick[r.id] && (qty[r.id] ?? 0) > 0);
  const orderCost = chosen.reduce((s, r) => s + (r.purchasePrice ?? 0) * (qty[r.id] ?? 0), 0);
  const counts = { out: rows.filter((r) => r.status === "out").length, low: rows.filter((r) => r.status === "low").length };

  const orderText = () => chosen.map((r) => `${r.nameRu}${r.sku ? ` (${r.sku})` : ""} — ${qty[r.id]} ${r.unit || "шт"}`).join("\n");
  const copy = async () => { try { await navigator.clipboard.writeText(orderText()); alert("Список скопирован"); } catch { alert("Не удалось скопировать"); } };
  const csv = () => {
    const head = ["Наименование", "SKU", "Остаток", "Продано за 30 дн.", "К заказу", "Ед."].join(";");
    const body = chosen.map((r) => [`"${r.nameRu.replace(/"/g, '""')}"`, r.sku ?? "", r.qty, r.sold30, qty[r.id], r.unit].join(";"));
    const blob = new Blob(["﻿" + [head, ...body].join("\r\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "zakaz-postavshiku.csv"; document.body.appendChild(a); a.click(); a.remove();
  };
  const print = () => {
    const w = window.open("", "_blank"); if (!w) return;
    const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c] as string));
    w.document.write(`<!doctype html><meta charset="utf-8"><title>Заказ поставщику</title><style>body{font-family:Arial,sans-serif;font-size:13px}table{border-collapse:collapse;width:100%}td,th{border:1px solid #999;padding:4px 8px;text-align:left}</style><h2>Заказ поставщику, ${new Date().toLocaleDateString("ru-RU")}</h2><table><tr><th>№</th><th>Наименование</th><th>SKU</th><th>Остаток</th><th>К заказу</th></tr>${chosen.map((r, i) => `<tr><td>${i + 1}</td><td>${esc(r.nameRu)}</td><td>${esc(r.sku ?? "")}</td><td>${r.qty}</td><td><b>${qty[r.id]} ${esc(r.unit || "шт")}</b></td></tr>`).join("")}</table><script>window.onload=function(){window.print()}</script>`);
    w.document.close();
  };

  return (
    <>
      <div className="ps-kpis">
        <div className="ps-kpi"><span>Закончились</span><b style={{ color: "#ef4444" }}>{counts.out}</b></div>
        <div className="ps-kpi warn"><span>Заканчиваются</span><b>{counts.low}</b></div>
        <div className="ps-kpi"><span>В заказ выбрано</span><b>{chosen.length}</b></div>
        {isAdmin && <div className="ps-kpi"><span>Сумма закупа</span><b>{money(orderCost)}</b></div>}
      </div>

      <div className="ps-card">
        <div className="ps-row2" style={{ marginBottom: 10 }}>
          <input className="ps-input" style={{ maxWidth: 320 }} placeholder="Поиск по названию или SKU" value={q} onChange={(e) => setQ(e.target.value)} />
          <label className="ps-check"><input type="checkbox" checked={only} onChange={(e) => setOnly(e.target.checked)} />Только требующие заказа</label>
          <span style={{ marginLeft: "auto" }} className="ps-row2">
            <button className="ps-btn" disabled={!chosen.length} onClick={copy}>📋 Скопировать</button>
            <button className="ps-btn" disabled={!chosen.length} onClick={csv}>⬇ CSV</button>
            <button className="ps-btn primary" disabled={!chosen.length} onClick={print}>🖨 Печать заказа</button>
          </span>
        </div>
        <div className="ps-hint" style={{ marginBottom: 8 }}>
          Прогноз по продажам за 30 дней (касса, сайт, Halyk, сделки). К заказу: запас на 21 день продаж или минимальный остаток, смотря что больше, за вычетом текущего остатка. Количество можно поправить.
        </div>
        <div className="ps-scroll">
          <table className="ps-table">
            <thead><tr><th></th><th>Товар</th><th>SKU</th><th className="num">Остаток</th><th className="num">Мин.</th><th className="num">Продано 30 дн.</th><th className="num">Хватит на</th><th>Статус</th><th className="num">К заказу</th></tr></thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="ps-muted">Загрузка…</td></tr>}
              {!loading && shown.length === 0 && <tr><td colSpan={9} className="ps-muted">Нет позиций</td></tr>}
              {shown.map((r) => (
                <tr key={r.id}>
                  <td><input type="checkbox" checked={!!pick[r.id]} onChange={(e) => setPick((p) => ({ ...p, [r.id]: e.target.checked }))} /></td>
                  <td>{r.nameRu}</td><td className="ps-muted">{r.sku ?? ""}</td>
                  <td className="num"><b>{r.qty}</b></td><td className="num">{r.minQuantity || "–"}</td><td className="num">{r.sold30 || "–"}</td>
                  <td className="num">{r.daysLeft == null ? "–" : `${r.daysLeft} дн.`}</td>
                  <td><span className={`ps-tag ${r.status}`}>{r.status === "out" ? "нет" : r.status === "low" ? "мало" : "ок"}</span></td>
                  <td className="num"><input className="ps-qty" type="number" min={0} value={qty[r.id] ?? 0} onChange={(e) => setQty((p) => ({ ...p, [r.id]: Math.max(0, Number(e.target.value) || 0) }))} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
